import { computeAnnualVolumeM3 } from '../../../../../../utils/consumptionCalculations.js';
import { PROFILES } from './monthlyNeedCalculations.js';

/**
 * Détecte, dans les usages/équipements réels de l'audit du bâtiment, les groupes
 * typiquement compatibles avec une réutilisation d'eau de pluie (WC, lave-linge,
 * arrosage). Le volume annuel de chaque groupe est la somme des consommations
 * réelles (computeAnnualVolumeM3), pas une estimation — mais reste librement
 * modifiable ensuite dans l'étape Besoins, comme n'importe quel autre usage.
 *
 * Il n'existe aucune donnée de répartition mensuelle réelle dans le modèle
 * (usage_data ne connaît que des compteurs annuels de semaines) : le profil
 * affecté ici est une heuristique par groupe, pas une mesure.
 */
export const ELIGIBLE_GROUPS = [
  {
    id: 'audit-wc',
    name: 'WC / sanitaires',
    profile: 'uniforme',
    sewage: true,
    category: 'wc',
    matches: (equipment, usage) => equipment?.type_equipment === 'wc',
  },
  {
    id: 'audit-washer',
    name: 'Lave-linge',
    profile: 'uniforme',
    sewage: true,
    category: 'washer',
    matches: (equipment, usage) => equipment?.type_equipment === 'machine' && equipment?.technical_data?.type_machine === 'washing_machine',
  },
  {
    id: 'audit-garden',
    name: 'Arrosage / jardin',
    profile: 'jardin',
    sewage: false,
    category: 'garden',
    // "Espaces verts" (catégorie usage_category_id) est le libellé actuel — "Arrosage"
    // n'est plus qu'un alias historique de migration, conservé en repli pour les
    // usages pas encore recatégorisés (usage_subcategory_id absent).
    matches: (equipment, usage) => usage?.usage_category_id === 'espaces_verts' || usage?.type_usage === 'Arrosage',
  },
];

/**
 * @param {Object[]} equipments
 * @param {Object[]} usages
 * @returns {Object[]} usages au format attendu par NeedsStep (mêmes champs que DEFAULT_USAGES),
 *   un par groupe éligible détecté, avec volume/nombre d'usages sources pour affichage.
 */
export const detectEligibleUsagesFromAudit = (equipments, usages) => {
  const equipmentById = new Map(equipments.map(e => [e._id, e]));

  return ELIGIBLE_GROUPS.map(group => {
    const matched = usages.filter(u => group.matches(equipmentById.get(u._id_equipement), u));
    if (matched.length === 0) return null;

    const volume = matched.reduce((sum, u) => sum + computeAnnualVolumeM3(equipmentById.get(u._id_equipement), u), 0);

    return {
      id: group.id,
      name: group.name,
      volume: Math.round(volume * 100) / 100,
      profile: group.profile,
      custom: group.profile === 'custom' ? PROFILES.jardin.slice() : null,
      enabled: true,
      sewage: group.sewage,
      category: group.category,
      sourceCount: matched.length,
      fromAudit: true,
    };
  }).filter(Boolean);
};
